import * as React from 'react'
import { View, Image } from 'react-native'
import { Card, Text, Button, IconButton, useTheme } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { PaypalPNG, WechatPaymentPNG } from '../../../../utils/meldd-icons'
import { createStyle } from '../../../../contexts/global-styles.provider'
import Row from '../../../../@ui/containers/grid/row'
import Column from '../../../../@ui/containers/grid/column'
import * as Sentry from '@sentry/react-native'
import Toast from 'react-native-root-toast'
import {
  useDisableUserTokenMutation,
  useSetDefaultPaymentTokenMutation,
  UserTokenDto,
} from '../../../../generated/graphql'

type PaymentMethodType = 'paypal' | 'wechat'

type PaymentMethodRowProps = {
  type: PaymentMethodType
  token?: UserTokenDto | null
  onConnect: () => void
  onDisconnect?: () => void
  onSetDefault?: () => void
}

export const PaymentMethodRow = ({
  type,
  token,
  onConnect,
  onDisconnect,
  onSetDefault,
}: PaymentMethodRowProps) => {
  const { t } = useTranslation('payment-methods')
  const theme = useTheme()
  const styles = useStyle()
  const [disableToken, { loading: disabling }] = useDisableUserTokenMutation()
  const [setDefaultToken, { loading: settingDefault }] =
    useSetDefaultPaymentTokenMutation()

  const isConnected = !!token
  const isDefault = token?.isDefault || false
  const tokenId = token?.id

  const getIcon = () => {
    if (type === 'paypal') {
      return <Image source={PaypalPNG} style={styles.icon} />
    }
    return <Image source={WechatPaymentPNG} style={styles.icon} />
  }

  const handleConnect = () => {
    onConnect()
  }

  const handleDelete = async () => {
    if (!tokenId) {
      return
    }

    try {
      await disableToken({
        variables: { id: tokenId },
      })
      Toast.show(t('disconnected-success'), {
        position: Toast.positions.CENTER,
      })
      if (onDisconnect) {
        onDisconnect()
      }
    } catch (error) {
      console.error('Error disabling token:', error)
      Sentry.captureException(error)
      Toast.show(t('disconnect-failed'), {
        position: Toast.positions.CENTER,
      })
    }
  }

  const handleSetDefault = async () => {
    if (!tokenId) {
      return
    }

    try {
      await setDefaultToken({
        variables: { id: tokenId },
      })
      Toast.show(t('set-default-success'), {
        position: Toast.positions.CENTER,
      })
      if (onSetDefault) {
        onSetDefault()
      }
    } catch (error) {
      console.error('Error setting default token:', error)
      Sentry.captureException(error)
      Toast.show(t('set-default-failed'), {
        position: Toast.positions.CENTER,
      })
    }
  }

  return (
    <Card style={styles.card}>
      <View style={styles.cardContent}>
        <Row justifyContent="space-between" alignItems="center">
          <Row columnGap={12} alignItems="center" flex={1}>
            <View style={styles.iconContainer}>{getIcon()}</View>
            <Column gap={2}>
              <Row columnGap={4} alignItems="center">
                <Text variant="titleMedium">{t(`${type}`)}</Text>
                {isDefault && (
                  <Text variant="labelSmall" style={styles.defaultBadge}>
                    {t('default')}
                  </Text>
                )}
              </Row>
              <Text variant="bodySmall" style={styles.statusText}>
                {isConnected ? t('connected') : t('not-connected')}
              </Text>
              {token?.errorMessage && (
                <Text variant="bodySmall" style={styles.errorText}>
                  {t('payment-error')}: {token.errorMessage}
                </Text>
              )}
            </Column>
          </Row>

          <View style={styles.actionContainer}>
            {isConnected ? (
              <Column gap={4} alignItems="flex-end">
                {!isDefault && (
                  <Button
                    mode="text"
                    onPress={handleSetDefault}
                    disabled={settingDefault}
                    compact
                    textColor={theme.colors.primary}
                  >
                    {t('set-default')}
                  </Button>
                )}
                <IconButton
                  icon="delete"
                  iconColor={theme.colors.error}
                  size={24}
                  onPress={handleDelete}
                  disabled={disabling}
                />
              </Column>
            ) : (
              <Button mode="contained" onPress={handleConnect} compact>
                {t('connect')}
              </Button>
            )}
          </View>
        </Row>
      </View>
    </Card>
  )
}

const useStyle = createStyle(({ theme }) => ({
  card: {
    marginVertical: theme.spacing(1),
  },
  cardContent: {
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(3),
  },
  iconContainer: {
    width: 48,
    height: 48,
    backgroundColor: 'white',
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    width: 32,
    height: 32,
  },
  statusText: {
    color: theme.colors.onSurfaceVariant,
  },
  actionContainer: {
    minWidth: 100,
    alignItems: 'flex-end',
  },
  defaultBadge: {
    backgroundColor: theme.colors.primaryContainer,
    color: theme.colors.onPrimaryContainer,
    paddingHorizontal: theme.spacing(1),
    paddingVertical: theme.spacing(0.5),
    borderRadius: theme.roundness,
  },
  errorText: {
    color: theme.colors.error,
    marginTop: theme.spacing(0.5),
  },
}))
